import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Receipt,
  Clock,
  AlertTriangle,
  FileWarning,
  Users,
  Building2,
  CreditCard,
  FileSpreadsheet,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { api } from '@/lib/api';
import { BrandLogo } from '@/components/BrandLogo';

interface NavItemConfig {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeKey?: 'pending' | 'alerts';
  matchPrefix?: boolean;
}

const workspaceItems: NavItemConfig[] = [
  { label: 'Home', href: '/', icon: LayoutDashboard },
  { label: 'Transactions', href: '/transactions', icon: Receipt },
  { label: 'Pending', href: '/pending', icon: Clock, badgeKey: 'pending' },
  { label: 'Alerts', href: '/alerts', icon: AlertTriangle, badgeKey: 'alerts' },
  { label: 'Parse failed', href: '/parse-failed', icon: FileWarning },
];

const adminItems: NavItemConfig[] = [
  { label: 'Users', href: '/admin/users', icon: Users, matchPrefix: true },
  { label: 'Departments', href: '/admin/departments', icon: Building2, matchPrefix: true },
  { label: 'Cards', href: '/admin/cards', icon: CreditCard, matchPrefix: true },
  { label: 'Statements', href: '/admin/statements', icon: FileSpreadsheet, matchPrefix: true },
];

export function AppSidebar() {
  const { pathname } = useLocation();

  const { data: pendingCount } = useQuery({
    queryKey: ['pending-count'],
    queryFn: () => api.listPending({ page: 1 }).then((r) => r.total_count),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const { data: alertsData } = useQuery({
    queryKey: ['alerts-count'],
    queryFn: () => api.countAlerts(),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const { data: meData } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () =>
      fetch('/api/auth/me', { credentials: 'include' }).then(
        (r) =>
          r.json() as Promise<{
            authenticated: boolean;
            user?: { id: number; email: string; name: string; role: string };
          }>,
      ),
    staleTime: 5 * 60_000,
  });

  const isAdmin = meData?.user?.role === 'admin';

  function getBadge(key?: 'pending' | 'alerts') {
    if (key === 'pending') return pendingCount && pendingCount > 0 ? pendingCount : undefined;
    if (key === 'alerts') {
      const n = alertsData?.high_count ?? alertsData?.open_count;
      return n && n > 0 ? n : undefined;
    }
    return undefined;
  }

  function renderItem(item: NavItemConfig) {
    const isActive = item.matchPrefix
      ? pathname.startsWith(item.href)
      : item.href === '/'
        ? pathname === '/'
        : pathname.startsWith(item.href);
    const badge = getBadge(item.badgeKey);
    const Icon = item.icon;
    return (
      <SidebarMenuItem key={item.href}>
        <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
          <Link to={item.href}>
            <Icon className="size-4" />
            <span>{item.label}</span>
          </Link>
        </SidebarMenuButton>
        {badge !== undefined && <SidebarMenuBadge>{badge}</SidebarMenuBadge>}
      </SidebarMenuItem>
    );
  }

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-3 px-2 py-2 group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0">
          <BrandLogo size={40} className="shrink-0 group-data-[collapsible=icon]:size-7" />
          <div className="flex flex-col leading-tight group-data-[collapsible=icon]:hidden">
            <span
              className="text-[15px] font-semibold text-foreground"
              style={{ letterSpacing: '0.02em' }}
            >
              CARD SYNC
            </span>
            <span className="text-[13px] font-normal text-muted-foreground">Kaopiz Holdings</span>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Workspace</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>{workspaceItems.map(renderItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {isAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>{adminItems.map(renderItem)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <div className="px-2 py-2 group-data-[collapsible=icon]:hidden">
          <p className="truncate font-mono text-[10px] text-muted-foreground">
            {meData?.user?.email ?? '…'}
          </p>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
